import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Typography, Alert, CircularProgress, Stack, Box,
  InputAdornment, IconButton, useMediaQuery, useTheme,
} from '@mui/material';
import { AttachFile as AttachIcon, Close as CloseIcon, CheckCircle as OkIcon, PictureAsPdf as PdfIcon } from '@mui/icons-material';
import { aprovadorApi, PanelItem, ActionResult, AprovadorError, brl, dataBR } from '@/services/aprovador';
import CodigoPagamento from './CodigoPagamento';

interface Props {
  token: string;
  item: PanelItem | null;
  onClose: () => void;
  onDone: () => void;
  onSessionLost: () => void;
}

const MAX = 5 * 1024 * 1024;

function lerBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).replace(/^data:[^,]+,/, ''));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

/** "Paguei no app": nº do documento do extrato e/ou anexo → recibo interno + baixa no app de origem. */
const PagueiDialog: React.FC<Props> = ({ token, item, onClose, onDone, onSessionLost }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [doc, setDoc] = useState('');
  const [valor, setValor] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ActionResult | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!item) return;
    setDoc(''); setFile(null); setError(null); setResult(null);
    setValor(item.amount.toFixed(2).replace('.', ','));
  }, [item?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const salvar = async () => {
    if (!item) return;
    if (!doc.trim() && !file) { setError('Informe o nº do documento do extrato ou anexe o comprovante.'); return; }
    if (file && file.size > MAX) { setError('O anexo passa de 5 MB.'); return; }
    setSaving(true);
    setError(null);
    try {
      const attachment = file ? { filename: file.name, mimeType: file.type || 'application/octet-stream', data: await lerBase64(file) } : undefined;
      const r = await aprovadorApi.paguei(token, item.id, { bankDocument: doc.trim() || undefined, paidAmount: valor, attachment });
      setResult(r);
      onDone();
    } catch (e) {
      const err = e as AprovadorError;
      if (err.status === 401) onSessionLost(); else setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!item} onClose={() => !saving && onClose()} fullWidth maxWidth="xs" fullScreen={fullScreen}>
      <DialogTitle sx={{ fontWeight: 700 }}>{result ? 'Baixa registrada' : 'Paguei no app'}</DialogTitle>
      <DialogContent dividers>
        {result ? (
          <Stack spacing={2} alignItems="center" textAlign="center" sx={{ py: 2 }}>
            <OkIcon color="success" sx={{ fontSize: 64 }} />
            <Typography>O pedido foi fechado como pago, o recibo interno foi gerado e o sistema de origem recebe a baixa.</Typography>
            {result.comprovante?.pdfUrl && (
              <Button variant="outlined" startIcon={<PdfIcon />} href={result.comprovante.pdfUrl} target="_blank" rel="noopener noreferrer">Ver recibo</Button>
            )}
          </Stack>
        ) : item && (
          <Stack spacing={2}>
            <Box>
              <Typography variant="h6" fontWeight={700}>{brl(item.amount)}</Typography>
              <Typography variant="body2" color="text.secondary">
                {item.beneficiaryName || 'Favorecido não informado'}{item.dueDate ? ` · vence ${dataBR(item.dueDate)}` : ''}
              </Typography>
            </Box>
            <CodigoPagamento item={item} />
            <Typography variant="body2" color="text.secondary">
              Depois de pagar no app do Sicoob, informe o nº do documento que aparece no extrato e/ou anexe o comprovante.
            </Typography>
            <TextField label="Nº do documento no extrato" value={doc} onChange={(e) => setDoc(e.target.value)} fullWidth
              inputProps={{ maxLength: 40, inputMode: 'numeric' }} />
            <TextField label="Valor pago" value={valor} onChange={(e) => setValor(e.target.value.replace(/[^\d,.]/g, ''))} fullWidth
              helperText="Se pagou com multa ou juros, ajuste para o valor debitado."
              InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} inputProps={{ inputMode: 'decimal' }} />
            <input ref={fileRef} type="file" accept="image/*,application/pdf" hidden
              onChange={(e) => { setFile(e.target.files?.[0] || null); e.target.value = ''; }} />
            {file ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, border: '1px solid #e3e8ef', borderRadius: 1, px: 1.5, py: 0.5 }}>
                <AttachIcon fontSize="small" color="action" />
                <Typography variant="body2" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</Typography>
                <IconButton size="small" onClick={() => setFile(null)}><CloseIcon fontSize="small" /></IconButton>
              </Box>
            ) : (
              <Button variant="outlined" startIcon={<AttachIcon />} onClick={() => fileRef.current?.click()}>Anexar comprovante (foto ou PDF)</Button>
            )}
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        {result ? (
          <Button fullWidth variant="contained" onClick={onClose}>Fechar</Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={saving}>Cancelar</Button>
            <Button variant="contained" onClick={salvar} disabled={saving || (!doc.trim() && !file)} sx={{ flex: 1 }}>
              {saving ? <CircularProgress size={22} color="inherit" /> : 'Confirmar pagamento no app'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PagueiDialog;
